/**
 * Turning a notification's absolute link into a route this app can navigate itself.
 *
 * <p>Its own file rather than a method on the bell, for the reason `board-card-link.ts` is one: this
 * is a single decision with a right answer worth testing on its own, and getting it wrong is
 * invisible — the link still works, it just reloads the entire SPA to land two feet from where it
 * started, losing every signal, every open connection and about two seconds.</p>
 *
 * <h2>Why the url is absolute in the first place</h2>
 *
 * <p>The server builds ONE url per notification and hands the same string to every channel. The
 * mention email needs an absolute one — a relative path means nothing in a mail client — so the bell,
 * the only reader that could have used a relative path, is the one that has to convert. That is the
 * right way round: the channel that can do the work does it.</p>
 *
 * <h2>What must NOT be navigated internally</h2>
 *
 * <p><b>Another origin.</b> A tenant on their own hostname, a link built on a tier's gateway and read
 * in a different app — routing those internally would ask this router for a path that does not exist
 * here and show a blank screen. They are followed as ordinary links instead.</p>
 *
 * <p><b>Anything outside the app's base href.</b> The SPA sits at `/web` behind the gateway and at
 * `/` standalone, so the same board card is `/web/boards/7?card=146` in one and `/boards/7?card=146`
 * in the other. The base has to be stripped before the router sees it, and a path that is NOT under
 * the base is not this application's to route.</p>
 */

/**
 * The in-app route for `url`, or null when it should be followed as an ordinary link.
 *
 * @param url the notification's `linkUrl`, absolute
 * @param origin where this app is running — `window.location.origin`
 * @param baseHref the app's base href, with or without its trailing slash (`/web/`, `/`, ``)
 */
export function internalRoute(
  url: string | undefined,
  origin: string,
  baseHref: string,
): string | null {
  if (!url) return null;

  let parsed: URL;

  try {
    parsed = new URL(url, origin);
  } catch {
    // Junk in the column. Nothing is navigated and nothing is followed — the caller renders no
    // button at all rather than one that does nothing.
    return null;
  }

  // A mailto:, a tel:, or anything else that is not the web. Never this router's business, and
  // `origin` on those parses as "null" rather than throwing.
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;

  if (parsed.origin !== origin) return null;

  const base = baseHref.replace(/\/+$/, '');

  if (base && parsed.pathname !== base && !parsed.pathname.startsWith(`${base}/`)) return null;

  // `|| '/'` is load-bearing: a link to the app root strips to the empty string, and
  // `navigateByUrl('')` does not navigate.
  const path = parsed.pathname.slice(base.length) || '/';

  return `${path}${parsed.search}${parsed.hash}`;
}
